import type { WorkspaceNavigationGuardService } from '.';
import { effect, signal } from '@preact/signals-react';
import { inject, injectable } from 'src/features/ioc';
import type { NavigationGuard, NavigationIntent } from 'src/services/NavigationService';
import { createRemoteSliceAction } from 'src/store/lib/createRemoteSlice';
import { confirmLeavingWorkspace } from 'src/store/overlays/confirmLeavingWorkspace';
import type { ReactBindings } from 'src/types/bindings';

interface ConfirmationAttempt {
    readonly promise: Promise<'allow' | 'deny'>;
    settle(decision: 'allow' | 'deny'): void;
}

@injectable()
export class WorkspaceNavigationGuardServiceImpl implements WorkspaceNavigationGuardService, NavigationGuard {
    readonly requiresConfirmation = signal(true);
    #confirmationAttempt: ConfirmationAttempt | null = null;
    readonly #disposeDirtyEffect: () => void;
    #registration: Disposable | null = null;

    constructor(
        @inject('Store') private readonly store: ReactBindings['Store'],
        @inject('NavigationService')
        private readonly navigationService: ReactBindings['NavigationService'],
    ) {
        this.#disposeDirtyEffect = effect(() => {
            const isDirty = Boolean(this.store.signal.value.workspace.currentWorkspace?.isDirty);
            if (isDirty && !this.#registration) {
                this.#registration = this.navigationService.registerGuard(this);
            } else if (!isDirty && this.#registration) {
                this.#registration[Symbol.dispose]();
                this.#registration = null;
            }
        });
    }

    confirm(intent: NavigationIntent): Promise<'allow' | 'deny'> {
        if (this.#confirmationAttempt) {
            return this.#confirmationAttempt.promise;
        }

        if (this.#staysInCurrentWorkspace(intent)) {
            return Promise.resolve('allow');
        }

        let resolveDecision: (decision: 'allow' | 'deny') => void;
        const promise = new Promise<'allow' | 'deny'>((resolve) => {
            resolveDecision = resolve;
        });
        let settled = false;
        let unsubscribe: (() => void) | null = null;
        const attempt: ConfirmationAttempt = {
            promise,
            settle: (decision) => {
                if (settled) return;
                settled = true;
                unsubscribe?.();
                if (this.#confirmationAttempt === attempt) {
                    this.#confirmationAttempt = null;
                }
                resolveDecision(decision);
            },
        };
        this.#confirmationAttempt = attempt;

        unsubscribe = this.store.subscribe(() => {
            const state = this.store.getState().remoteSlice.confirmLeavingWorkspace as {
                readonly isOpen: boolean;
                readonly lastCloseResult: 'cancel' | 'dont-save' | 'save' | 'saved' | null;
            };
            // This guard owns the modal opening, so the first close action
            // is authoritative even when open and close dispatch back-to-back.
            if (state.isOpen || state.lastCloseResult === null) return;

            attempt.settle(state.lastCloseResult === 'cancel' ? 'deny' : 'allow');
        });

        this.store.dispatch(createRemoteSliceAction(confirmLeavingWorkspace, 'openModal'));
        return promise;
    }

    #staysInCurrentWorkspace(intent: NavigationIntent): boolean {
        if (intent.kind !== 'navigate' || !intent.to) return false;

        const workspaceId = this.store.signal.value.workspace.currentWorkspace?.id;
        const [, route, destinationWorkspaceId] = intent.to.pathname.split('/');
        return Boolean(workspaceId && route === 'dashboard' && destinationWorkspaceId === workspaceId);
    }

    [Symbol.dispose](): void {
        this.#disposeDirtyEffect();
        this.#confirmationAttempt?.settle('deny');
        this.#registration?.[Symbol.dispose]();
        this.#registration = null;
    }
}
